import { useResponsive } from "@/hooks/useBreakpoints";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useDeleteDataFileMutation,
  useLazyGetDataFilesQuery,
} from "@/redux/service";
import { DateTime } from "luxon";
import { ColumnType } from "antd/es/table";

import DataFilesFilters from "./DataFilesFilters";
import CancelConfirmationModal from "@/components_v2/Modal/CancelConfirmationModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import QueryTable from "@/components_v2/Tables/QueryTable";
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { BaseUser } from "@/views/profiles/types/user";
import UserAvatar from "@/components_v2/Avatar/UserAvatar";
import { ColumnKey } from "@/views/datablocks/types/types";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import DataFileReferences from "@/views/files/components/DataFileReferences";
import { DataFile } from "@/redux/files/types";
import {
  DataFilesFiltersState,
  DataFilesTableProps,
} from "@/views/files/types";

import "./styles.css";

const DataFilesTable = ({
  columnNames,
  filterNames,
  defaultFilters = {},
}: DataFilesTableProps) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();

  const [deleteDataFile] = useDeleteDataFileMutation();
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedFileId, setSelectedFileId] = useState<number | undefined>();
  const [filters, setFilters] = useState<DataFilesFiltersState>(
    defaultFilters as DataFilesFiltersState,
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

  const handleRemoveItem = (index: number) => {
    deleteDataFile(index);
    setOpenConfirmDeleteModal(false);
  };

  const handleDownloadFile = (file: DataFile) => {
    const link = document.createElement("a");
    link.href = file.downloadUrl;
    link.download = file.name || "file";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRowExpand = (fileId: number) => {
    if (expandedRowKeys.includes(fileId)) {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key !== fileId));
    } else {
      setExpandedRowKeys([...expandedRowKeys, fileId]);
    }
  };

  const columns: Record<string, ColumnType<DataFile>> = useMemo(
    () => ({
      name: {
        title: t("table.title"),
        fixed: !isMobile ? "left" : undefined,
        width: 450,
        ellipsis: true,
        dataIndex: "name",
        render: (value: string) => (
          <span className="data-files-table-title-container data-file-title body-s-regular">
            {value}
          </span>
        ),
      },
      extension: {
        title: t("table.fileType"),
        dataIndex: "extension",
        align: "center",
        render: (value: string) => (
          <p className="data-files-table-title-container body-s-regular">
            {value.replace(".", "").toUpperCase()}
          </p>
        ),
      },
      uploadedBy: {
        title: t("datablocks.labels.uploadedBy"),
        dataIndex: "createdBy",
        align: "center",
        render: (value: BaseUser) => (
          <UserAvatar user={value} tooltip={value.fullName} />
        ),
      },
      uploadedAt: {
        title: t("table.dateUploaded"),
        dataIndex: "createdAt",
        align: "center",
        render: (value: string) => (
          <p className="data-files-table-title-container body-s-regular">
            {DateTime.fromISO(value).toFormat("dd MMMM yyyy")}
          </p>
        ),
      },
      references: {
        title: t("table.references"),
        dataIndex: "referencesCount",
        align: "center",
        render: (value: number, file: DataFile) => (
          <p className="data-files-table-title-container body-s-regular">
            {t("datablocks.labels.referencesCount", {
              count: value,
              plural: value === 1 ? "" : "s",
            })}
            {file.referencesCount > 0 && (
              <div>
                <PlusOutlined
                  style={{
                    transform: expandedRowKeys.includes(file.id)
                      ? "rotate(45deg)"
                      : "rotate(0deg)",
                    transition:
                      "transform 0.3s cubic-bezier(0.645, 0.045, 0.355, 1)",
                    cursor: "pointer",
                  }}
                  onClick={() => handleRowExpand(file.id)}
                />
              </div>
            )}
          </p>
        ),
      },
      actions: {
        title: t("table.actions"),
        align: "center",
        render: (value: DataFile) => (
          <div className="data-files-table-actions">
            <BaseTooltip title={t("datablocks.labels.viewFile")}>
              <BaseButton
                variant="link"
                leadingIcon={<EyeOutlined />}
                onClick={() =>
                  window.open(value.pdfFile || value.file, "_blank")
                }
              />
            </BaseTooltip>
            <BaseTooltip title={t("datablocks.labels.downloadFile")}>
              <BaseButton
                variant="link"
                leadingIcon={<DownloadOutlined />}
                onClick={() => handleDownloadFile(value)}
              />
            </BaseTooltip>
            <BaseTooltip
              title={
                value.referencesCount > 0
                  ? t("datablocks.labels.canNotDeleteFileWithReferences")
                  : t("datablocks.labels.deleteFile")
              }
            >
              <BaseButton
                disabled={value.referencesCount > 0}
                variant="link"
                leadingIcon={<DeleteOutlined />}
                onClick={() => {
                  setOpenConfirmDeleteModal(true);
                  setSelectedFileName(value.name);
                  setSelectedFileId(value.id);
                }}
              />
            </BaseTooltip>
          </div>
        ),
      },
    }),
    [isMobile, t, expandedRowKeys],
  );

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) => columns[columnKey as ColumnKey] as ColumnType<DataFile>,
      ),
    [columns, columnNames],
  );

  return (
    <div className="data-blocks-table">
      <DataFilesFilters
        defaultFilters={defaultFilters}
        filterNames={filterNames}
        onFiltersChange={setFilters}
      />

      <QueryTable<DataFile>
        query={useLazyGetDataFilesQuery}
        filters={filters}
        columns={mappedColumns}
        expandable={{
          showExpandColumn: false,
          expandedRowKeys: expandedRowKeys,
          rowExpandable: (file) => file.referencesCount > 0,
          expandedRowRender: (file) => <DataFileReferences fileId={file.id} />,
        }}
      />
      <CancelConfirmationModal
        open={openConfirmDeleteModal}
        onCancel={() => setOpenConfirmDeleteModal(false)}
        onConfirm={() =>
          selectedFileId !== undefined && handleRemoveItem(selectedFileId)
        }
        title={t("confirm.confirmDelete")}
        confirmButtonText={t("buttons.delete")}
        content={t("datablocks.labels.confirmDeleteModalContent", {
          name: selectedFileName,
        })}
      />
    </div>
  );
};

export default DataFilesTable;
